import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import routeVariants from "../../assets/animates/routeVariants";
import Footer from "../global/Footer";
import NavbarBottom from "../global/NavbarBottom";
import icFile from "../../assets/images/pictures/icon/ic_file_24px.svg";
import icPlayCircle from "../../assets/images/pictures/icon/ic_outline-play_circle_filled_white_circle-40px.svg";
import icPaperCircle from "../../assets/images/pictures/icon/ic_paper_circle.svg";
import faqItems, { renderFaqAnswer, pickLang } from "./data/faqItems";
import pageCopy, { pickLang as pickPageLang } from "./data/pageCopy";
import "./QAContent.css";

const categoryKeys = ["all", "legal", "operation", "provider", "solution"];

const QAContent = () => {
    const { t, i18n } = useTranslation();
    const [activeCategory, setActiveCategory] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [openIds, setOpenIds] = useState([]);

    const lang = (i18n.language || "").toLowerCase().startsWith("en") ? "en" : "vi";
    const journeySteps = pickPageLang(pageCopy.journeySteps, lang);
    const lawChips = pickPageLang(pageCopy.lawChips, lang);
    const heroNotice = pickPageLang(pageCopy.heroNotice, lang);
    const referenceSources = pickPageLang(pageCopy.referenceSources, lang);

    useEffect(() => {
        setOpenIds([]);
        setActiveCategory("all");
        setSearchTerm("");
    }, [lang]);

    const visibleFaqs = useMemo(() => {
        const keyword = searchTerm.trim().toLowerCase();

        return faqItems.filter((item) => {
            const question = pickLang(item.question, lang);
            const answerHtml = renderFaqAnswer(item, lang);
            const byCategory = activeCategory === "all" || item.category === activeCategory;
            const byKeyword =
                !keyword ||
                question.toLowerCase().includes(keyword) ||
                answerHtml.toLowerCase().includes(keyword);

            return byCategory && byKeyword;
        });
    }, [activeCategory, searchTerm, lang]);

    return (
        <motion.div
            variants={routeVariants}
            initial="initial"
            animate="final"
            id="content"
            className="fs-navigation-overlay-content fs-navigation-overlay-left-content fs-navigation__0 no-transform"
        >
            <div className="container-fuild background-solution-image">
                <div className="container px-md-0 pt-50 pb-50">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <h2 className="text-center font-weight-bold mx-auto text-yellow font-60">
                                {t("qa-page.qa-page-content.content-1.heading")}
                            </h2>
                            <p className="mt-25 mb-20 text-white qa-hero-lead">
                                {t("qa-page.qa-page-content.content-1.description")}
                            </p>

                            <div className="qa-law-chips">
                                {lawChips.map((chip) => (
                                    <span key={chip}>{chip}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="solutions-page bg-color-solution qa-page-body">
                <div className="container description px-md-0 pt-40">
                    <div className="qa-page-notice">
                        <strong>{t("qa-page.notice-label")}</strong> {heroNotice}
                    </div>
                </div>

                <div className="container description px-md-0 pt-40">
                    <div className="row">
                        <div className="col-md-8 col-md-offset-2 text-center mb-40">
                            <h2 className="font-weight-bold">
                                {t("qa-page.journey-heading")}
                            </h2>
                        </div>
                    </div>

                    <div className="qa-journey-row mb-50">
                        {journeySteps.map((step, index) => (
                            <div key={step.title} className="qa-journey-col">
                                <div className="qa-journey-item">
                                    <div className="qa-journey-number">
                                        {String(index + 1).padStart(2, "0")}
                                    </div>
                                    <h4 className="font-weight-bold">{step.title}</h4>
                                    <p>{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="container description px-md-0 pb-50">
                    <div className="qa-section-head">
                        <div>
                            <h2 className="font-weight-bold">
                                {t("qa-page.faq-section-title")}
                            </h2>
                            <p>{t("qa-page.faq-section-desc")}</p>
                        </div>
                        <div className="qa-result-count">
                            {t("qa-page.result-count", { count: visibleFaqs.length })}
                        </div>
                    </div>

                    <div className="qa-filters" aria-label={t("qa-page.filter-aria")}>
                        {categoryKeys.map((key) => (
                            <button
                                key={key}
                                type="button"
                                className={`qa-filter-btn${
                                    activeCategory === key ? " active" : ""
                                }`}
                                onClick={() => setActiveCategory(key)}
                            >
                                {t(`qa-page.categories.${key}`)}
                            </button>
                        ))}
                    </div>

                    <div className="mb-20">
                        <input
                            type="search"
                            className="form-control"
                            placeholder={t("qa-page.search-placeholder")}
                            aria-label={t("qa-page.search-aria")}
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </div>

                    <div className="qa-faq-list">
                        {visibleFaqs.map((item) => {
                            const itemId = `faq-${item.number}-${item.category}`;
                            const isOpen = openIds.includes(itemId);
                            const badgeClass =
                                item.category === "solution"
                                    ? "qa-faq-badge--green"
                                    : "qa-faq-badge--navy";
                            const question = pickLang(item.question, lang);
                            const answerHtml = renderFaqAnswer(item, lang);

                            return (
                                <div
                                    key={itemId}
                                    className={`qa-faq-card${isOpen ? " is-open" : ""}`}
                                >
                                    <button
                                        type="button"
                                        className="qa-faq-header"
                                        onClick={() =>
                                            setOpenIds((prev) =>
                                                isOpen
                                                    ? prev.filter((id) => id !== itemId)
                                                    : [...prev, itemId]
                                            )
                                        }
                                        aria-expanded={isOpen}
                                    >
                                        <span className={`qa-faq-badge ${badgeClass}`}>
                                            {String(item.number).padStart(2, "0")}
                                        </span>
                                        <span className="qa-faq-question">{question}</span>
                                        <span
                                            className={`qa-faq-chevron${
                                                isOpen ? "" : " collapsed"
                                            }`}
                                        >
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="14"
                                                height="14"
                                                fill="none"
                                                stroke="#1a2e6b"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="qa-faq-body">
                                            <div
                                                className="qa-answer"
                                                dangerouslySetInnerHTML={{
                                                    __html: answerHtml,
                                                }}
                                            />
                                            {item.resources?.length > 0 && (
                                                <div className="qa-related">
                                                    <div className="qa-related-head">
                                                        <img
                                                            src={icFile}
                                                            alt=""
                                                            width="22"
                                                            height="22"
                                                        />
                                                        <div>
                                                            <h3>{t("qa-page.related-title")}</h3>
                                                            <p>{t("qa-page.related-desc")}</p>
                                                        </div>
                                                    </div>
                                                    <div className="qa-related-list">
                                                        {item.resources.map((resource) => (
                                                            <a
                                                                key={`${resource.type}-${resource.href}`}
                                                                className={`qa-related-card qa-related-card--${resource.type}`}
                                                                href={resource.href}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <span className="qa-related-icon">
                                                                    <img
                                                                        src={
                                                                            resource.type ===
                                                                            "video"
                                                                                ? icPlayCircle
                                                                                : icPaperCircle
                                                                        }
                                                                        alt=""
                                                                        width="28"
                                                                        height="28"
                                                                    />
                                                                </span>
                                                                <span className="qa-related-copy">
                                                                    <strong>
                                                                        {pickLang(
                                                                            resource.title,
                                                                            lang
                                                                        )}
                                                                    </strong>
                                                                    <span>
                                                                        {pickLang(
                                                                            resource.description,
                                                                            lang
                                                                        )}
                                                                    </span>
                                                                </span>
                                                                <span
                                                                    className="qa-related-arrow"
                                                                    aria-hidden="true"
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        width="18"
                                                                        height="18"
                                                                        fill="none"
                                                                        stroke="#1a2e6b"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    >
                                                                        <path d="M7 17L17 7" />
                                                                        <path d="M8 7h9v9" />
                                                                    </svg>
                                                                </span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <section className="qa-sources-panel">
                        <h2 className="font-weight-bold">{t("qa-page.sources-title")}</h2>
                        <div className="qa-sources-grid">
                            {referenceSources.map((source) => (
                                <a
                                    key={source.label}
                                    href={source.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {source.label}
                                </a>
                            ))}
                        </div>
                    </section>

                    <section className="qa-cta">
                        <div className="qa-cta-grid">
                            <div className="qa-cta-copy">
                                <h2>{t("qa-page.cta-title")}</h2>
                                <p>{t("qa-page.cta-desc")}</p>
                            </div>
                            <div className="qa-cta-links">
                                <Link
                                    to="/digital-signature"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t("qa-page.cta-solution")}
                                </Link>
                                <a href="tel:19006884">{t("qa-page.cta-hotline")}</a>
                                <a href="mailto:info@quantera.vn">{t("qa-page.cta-email")}</a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <Footer />
            <NavbarBottom />
        </motion.div>
    );
};

export default QAContent;
